import type { ReactElement } from 'react';
import React from 'react';
import type { FilterType } from 'react-table';
import type { GroupBase, Props as SelectProps } from 'atom-components/select/select';
import Select from 'atom-components/select/select';
import type { FilterBuilder, FilterRenderer } from './types';

type SelectFilterProps<Option, Group extends GroupBase<Option> = GroupBase<Option>> = Omit<
  SelectProps<Option, boolean, Group>,
  'onChange' | 'value'
>;

export const selectFilterFn =
  <Option,>(getOptionValue?: SelectFilterProps<Option>['getOptionValue']): FilterType<object> =>
  (rows, ids, filterValue) => {
    // make it array for more consistent searching
    const filterArray = Array.isArray(filterValue) ? filterValue : [filterValue];
    return filterArray?.length
      ? rows.filter((row) =>
          ids.some((id) =>
            filterArray.some((filter) => {
              const searchedValue = getOptionValue ? getOptionValue(filter) : filter.value;
              const value = row.values[id];

              return Array.isArray(row.values[id])
                ? value?.includes(searchedValue) || (!searchedValue && !value?.length)
                : value === searchedValue;
            })
          )
        )
      : rows;
  };

selectFilterFn.autoRemove = (val: boolean): boolean => !val;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SelectColumnFilter = <Option,>({ ...selectProps }: SelectFilterProps<Option, any>) =>
  // eslint-disable-next-line func-names
  function ({ column: { filterValue, setFilter } }: FilterRenderer): ReactElement {
    return (
      <Select
        {...selectProps}
        value={filterValue}
        onChange={(options: Option[]): void =>
          setFilter(options?.length || options ? options : undefined)
        }
        menuPosition="fixed"
      />
    );
  };

export const SelectFilter = <Option, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: SelectFilterProps<Option, Group>
): FilterBuilder => ({
  filterType: 'multiSelectFilter',
  Filter: SelectColumnFilter<Option>(props),
  filter: selectFilterFn<Option>(props?.getOptionValue)
});
